import type { KeyboardEventHandler } from 'react';
import type { NativeElementProps } from '../../types.js';
import { useTableRowState } from '../contexts/row-state.js';
import { useTableState } from '../contexts/table-state.js';

export interface UseSelectableRow {
  rowProps: NativeElementProps<HTMLTableRowElement>;
}

export function useSelectableRow(): UseSelectableRow {
  const { rowSelectionMode, setPendingRowSelectionState } = useTableState();
  const { isSelected, onSelectionToggle } = useTableRowState();

  // Handles row selection using a keyboard.
  const onKeyDownCapture: KeyboardEventHandler = (e) => {
    // Ignore keyboard events bubbling through portals
    if (!e.currentTarget.contains(e.target as Element)) {
      return;
    }

    if (e.key === ' ' && e.shiftKey) {
      // Toggle selection of current row
      onSelectionToggle?.();
    } else if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && e.shiftKey && rowSelectionMode === 'multiple') {
      // Extend selection up/down by one row
      // We're not going to stop propagation so that the arrow navigation handler runs as usual,
      // and we'll set a special state variable so that the selection state gets updated when the new row receives focus.
      setPendingRowSelectionState(isSelected);
      return;
    } else {
      // if we didn't handle anything, return early so we don't stop propagation and preventDefault
      return;
    }

    e.preventDefault();
    e.stopPropagation();
  };

  const rowProps: NativeElementProps<HTMLTableRowElement> = {
    onKeyDownCapture: rowSelectionMode !== 'none' ? onKeyDownCapture : undefined,
  };

  return {
    rowProps,
  };
}
